import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";
import { dohvatiDokumente, type DokumentDjelatnika } from "@/lib/dokumenti";
import { datum } from "@/lib/format";

const DESTINATIONS: Record<string, { tab: string; name: string; permission: string }> = {
  dozvola: { tab: "dozvole", name: "Dozvole", permission: "dozvola" },
  zdravstvena_sposobnost: { tab: "zdravlje", name: "Zdravlje", permission: "zdravlje" },
  osposobljavanje_znr: { tab: "znr", name: "Zaštita na radu", permission: "znr" },
  ozo_zaduzenje: { tab: "znr", name: "Zaštitna oprema", permission: "znr" },
  vozacka_dozvola: { tab: "identitet", name: "Vozačka dozvola", permission: "dozvola" },
  identifikacijski_dokument: { tab: "identitet", name: "Identifikacijski dokumenti", permission: "dozvola" },
  kvalifikacija: { tab: "kvalifikacije", name: "Kvalifikacije", permission: "radnik" },
  employee_employment_terms: { tab: "payroll", name: "Ugovor", permission: "payroll_profile" },
};

export function StructuredRecordLink({ document }: { document: DokumentDjelatnika }) {
  const { can } = useAuth();
  const destination = DESTINATIONS[document.structured_record_kind ?? ""];
  if (!destination || !document.structured_record_id || !can(destination.permission, "read") || document.status === "deleted") return null;
  const evidenceQuery = document.structured_record_kind === "employee_employment_terms" ? `&document=${encodeURIComponent(document.id)}` : "";
  return <Link className="block text-sm underline" to={`/zaposlenici/${document.radnik_id}?tab=${destination.tab}${evidenceQuery}#record-${document.structured_record_id}`}>→ {document.structured_record_label ?? `${destination.name}: ${document.document_type_name ?? "zapis"}${document.trajno ? ", Trajno" : document.expires_on ? `, vrijedi do ${datum(document.expires_on)}` : ""}`}</Link>;
}

const EvidenceContext = createContext<DokumentDjelatnika[]>([]);
export function StructuredEvidenceProvider({ radnikId, enabled, children, includeHealth = false, refreshKey = 0 }: { radnikId: string; enabled: boolean; children: ReactNode; includeHealth?: boolean; refreshKey?: number }) {
  const { can } = useAuth();
  const [documents, setDocuments] = useState<DokumentDjelatnika[]>([]);
  const allowed = enabled && can("dokument", "read");
  useEffect(() => {
    if (!allowed) return;
    const controller = new AbortController();
    dohvatiDokumente(radnikId, controller.signal, false, includeHealth).then((items) => { if (!controller.signal.aborted) setDocuments(items); }).catch(() => { /* The record remains readable even when evidence is unavailable. */ });
    return () => controller.abort();
  }, [radnikId, allowed, includeHealth, refreshKey]);
  return <EvidenceContext.Provider value={allowed ? documents.filter((item) => item.radnik_id === radnikId) : []}>{children}</EvidenceContext.Provider>;
}
export function StructuredEvidence({ kind, recordId }: { kind: string; recordId?: string }) {
  const documents = useContext(EvidenceContext);
  const matches = documents.filter((item) => item.structured_record_kind === kind && item.structured_record_id === recordId && item.status !== "deleted" && item.has_content);
  return <>{matches.map((item) => <Link key={item.id} className="block text-sm underline" to={`/zaposlenici/${item.radnik_id}?tab=dokumenti&document=${item.id}`}>Dokaz: {item.naziv || item.original_filename || item.document_type_name || "Dokument"}</Link>)}</>;
}
